import React, { useEffect, useState } from 'react';
import {
  Button,
  Modal,
  Input,
  Row,
  Col,
  Select,
  DatePicker,
  Form,
  Spin,
  Typography,
  Segmented,
  message,
} from 'antd';
import { LinkOutlined, UserAddOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import I18n from 'i18n-js';
import useErrors from 'hooks/forms/useErrors';
import dayjs from 'dayjs';
import {
  ticket_path,
  tickets_path,
  related_attachments_ticket_path,
} from 'js-routes/generated/routes';
import {
  arrayOf, string, func, bool,
} from 'prop-types';
import useDebounceAdminUserFetch from './hooks/useDebounceAdminUserFetch';
import useDebounceAssigneeUserFetch from './hooks/useDebounceAssigneeUserFetch';
import useSubmitForm from './hooks/useSubmitForm';
import useTicketAssigneeDefault from './hooks/useTicketAssigneeDefault';
import useReviewDecision from './hooks/useReviewDecision';
import { ticketPageTitle } from './helpers/ticketPageTitle';
import DetailedInfo from './DetailedInfo';
import PriorityLabel from './nested/PriorityLabel';
import StatusLabel from './nested/StatusLabel';
import CwoModuleLabel from './nested/CwoModuleLabel';
import UserWithAvatarLabel from './nested/UserWithAvatarLabel';
import TicketTypeLabel from './nested/TicketTypeLabel';
import SizeLabel from './nested/SizeLabel';
import DevStatusLabel from './nested/DevStatusLabel';
import DevStageLabel from './nested/DevStageLabel';
import CommitmentLevelLabel from './nested/CommitmentLevelLabel';
import { SMALL_DEVICE_WIDTH, ASSIGNEE_SEARCH_MIN_LENGTH } from './lib/constants';
import Attachments from './Attachments';
import ExternalLinks from './ExternalLinks';
import JiraLinkField from './JiraLinkField';
import TicketContextInfo from './nested/TicketContextInfo';
import FormActions from './nested/FormActions';
import {
  responsibleUserInfoDefaultPropTypes,
  currentUserInfoDefaultPropTypes,
  responsibleUserInfoPropTypes,
  currentUserInfoPropTypes,
  selectedNodePropTypes,
  selectedNodeDefaultPropTypes,
} from './propTypes';

const { TextArea } = Input;
const { Text } = Typography;

const TicketModalForm = ({
  priorities = [],
  statuses = [],
  ticketTypes = [],
  selectedNode = selectedNodeDefaultPropTypes,
  setSelectedNode = (_node) => {},
  currentUserInfo = currentUserInfoDefaultPropTypes,
  setInitialTickets = (_tickets) => {},
  isModalOpen = false,
  setIsModalOpen = (_open) => {},
  ticketFromDashboard = false,
  responsibleUserInfo = responsibleUserInfoDefaultPropTypes,
  defaultBugAssigneeInfo = responsibleUserInfoDefaultPropTypes,
  commitmentLevels = [],
  devStages = [],
  devStatuses = [],
  sizes = [],
  cwoModules = [],
  canSetInternalComment = false,
}) => {
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState(window.innerWidth < SMALL_DEVICE_WIDTH);
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [hasUnsavedComment, setHasUnsavedComment] = useState(false);
  const [externalLinks, setExternalLinks] = useState(selectedNode?.externalLinks || []);
  const currentTicketType = Form.useWatch('ticket_type', form);
  const currentAssigneeId = Form.useWatch('assignee_id', form);
  const currentAssigneeType = Form.useWatch('assignee_type', form);
  const currentStatus = Form.useWatch('status', form);

  const resolveFormUrl = () => {
    if (selectedNode?.id) {
      return ticket_path(selectedNode.id);
    }
    return tickets_path();
  };

  const {
    handleError,
    clearErrors,
    errorStatus,
    errorMessage,
  } = useErrors();

  const userLabel = (username, avatarUrl) => (
    <UserWithAvatarLabel
      username={username}
      avatarUrl={avatarUrl}
    />
  );

  const {
    initialAssignee,
    handleAssigneeChange,
    selectAssignee,
    resetAssigneeChoice,
  } = useTicketAssigneeDefault({
    form,
    ticketType: currentTicketType,
    isNewTicket: !selectedNode?.id,
    responsibleUserInfo,
    defaultBugAssigneeInfo,
    buildLabel: userLabel,
  });

  const {
    isFormSending,
    handleSubmitForm,
    submitButtonName,
  } = useSubmitForm({
    form,
    formUrl: resolveFormUrl(),
    clearErrors,
    handleError,
    id: selectedNode?.id,
    setIsModalOpen,
    setSelectedNode,
    setInitialTickets,
    ticketFromDashboard,
    attachments,
    setAttachments,
    externalLinks,
    resetAssigneeChoice,
  });

  const {
    isDecisionSending,
    submitDecision,
  } = useReviewDecision({
    form,
    ticketId: selectedNode?.id,
    setInitialTickets,
    handleError,
  });

  const {
    adminUserFetch,
    dataFetching,
    adminUsers,
  } = useDebounceAdminUserFetch({ initialOptions: [] });

  const {
    assigneeUserFetch,
    searchValue: assigneeSearchValue,
    dataFetching: assigneeFetching,
    assigneeUsers,
  } = useDebounceAssigneeUserFetch({ initialOptions: [] });

  const assigneeHint = I18n.t('search_with_min_input', { count: ASSIGNEE_SEARCH_MIN_LENGTH });

  const assigneeNotFoundContent = () => {
    if (assigneeFetching) return <Spin size="small" />;

    return assigneeSearchValue.length < ASSIGNEE_SEARCH_MIN_LENGTH
      ? assigneeHint
      : I18n.t('shared.no_results_found');
  };

  useEffect(() => {
    if (selectedNode?.id) {
      setIsModalOpen(true);
      history.replaceState({}, '', ticket_path(selectedNode.id));
      document.title = ticketPageTitle(selectedNode.id);
      /* eslint-disable camelcase */
      form.setFieldsValue({
        title: selectedNode.title,
        description: selectedNode.description,
        priority: selectedNode.priority,
        status: selectedNode.status,
        ticket_type: selectedNode.ticketType,
        author_id: selectedNode.author
          ? {
            value: selectedNode.author.id,
            label: userLabel(selectedNode.author.username, selectedNode.author.avatarUrl),
          }
          : null,
        author_type: selectedNode.author
          ? selectedNode.author.type
          : '',
        assignee_id: selectedNode.assignee
          ? {
            value: selectedNode.assignee.id,
            label: userLabel(selectedNode.assignee.username, selectedNode.assignee.avatarUrl),
          }
          : null,
        assignee_type: selectedNode.assignee
          ? selectedNode.assignee.type
          : '',
        responsible_user_id: selectedNode.responsibleUser
          ? {
            value: selectedNode.responsibleUser.id,
            label: userLabel(
              selectedNode.responsibleUser.username,
              selectedNode.responsibleUser.avatarUrl,
            ),
          }
          : null,
        due_date: selectedNode.dueDate
          ? dayjs(selectedNode.dueDate)
          : null,
        cwo_module: selectedNode.cwoModule || '',
        company_id: selectedNode.companyId,
        commitment_level: selectedNode.commitmentLevel || null,
        dev_stage: selectedNode.devStage || null,
        dev_status: selectedNode.devStatus || null,
        size: selectedNode.size || null,
        cwo_context: selectedNode.cwoContext || { referrer_url: location.href },
        jira_link: selectedNode.jiraLink || '',
      });
      /* eslint-enable camelcase */
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setExternalLinks(selectedNode?.externalLinks || []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, selectedNode]);

  useEffect(() => {
    if (!selectedNode?.id) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setAttachments([]);
      return;
    }

    setAttachmentsLoading(true);
    const loadAttachments = async () => {
      const response = await fetch(related_attachments_ticket_path(selectedNode.id));
      const data = await response.json();
      setAttachments(data.attachments);
      setAttachmentsLoading(false);
    };
    loadAttachments();
  }, [selectedNode?.id]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < SMALL_DEVICE_WIDTH);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const warnAboutUnsavedComment = () => {
    message.warning(I18n.t('tickets.form.unsaved_comment'));
  };

  const handleModalClose = () => {
    if (hasUnsavedComment) {
      warnAboutUnsavedComment();
      return;
    }

    setIsModalOpen(false);

    if (!selectedNode?.id) return;

    history.replaceState({}, '', tickets_path());
    document.title = ticketPageTitle();
    setSelectedNode(null);
    setAttachments([]);
    setExternalLinks([]);
    form.resetFields();
    resetAssigneeChoice();
  };

  const handleFinish = (values) => {
    if (hasUnsavedComment) {
      warnAboutUnsavedComment();
      return;
    }

    handleSubmitForm(values);
  };

  const handleCopyTicketLink = () => {
    const ticketUrl = new URL(ticket_path(selectedNode.id), location.origin).href;
    navigator.clipboard.writeText(ticketUrl);
    message.success(I18n.t('tickets.form.ticket_link_copied'));
  };

  const isAdmin = currentUserInfo.type === 'AdminUser';
  const canManageJiraLink = currentUserInfo.fullAdmin;
  const isNewTicket = !selectedNode?.id;
  const useSingleColumn = !isAdmin && isNewTicket;
  const isAssignedToCurrentUser = currentAssigneeId?.value === currentUserInfo.id
    && currentAssigneeType === currentUserInfo.type;
  const showAssignToMe = isAdmin && !isAssignedToCurrentUser;
  const assigneeFormItemClassName = classNames(
    'tickets-index-page__form-item',
    { 'tickets-index-page__form-item--no-bottom-margin': showAssignToMe },
  );

  const handleCopyTenant = () => {
    navigator.clipboard.writeText(selectedNode.tenant);
    message.success(I18n.t('tickets.form.tenant_copied'));
  };

  const resolveModalTitle = () => {
    if (!selectedNode?.id) return I18n.t('tickets.form.new_ticket');

    const subtitleText = `${selectedNode.companyName} · ${selectedNode.country}`;
    const subtitle = isAdmin ? (
      <button
        type="button"
        className="tickets-index-page__modal-subtitle tickets-index-page__modal-subtitle--clickable"
        onClick={handleCopyTenant}
      >
        {subtitleText}
      </button>
    ) : (
      <div className="tickets-index-page__modal-subtitle">{subtitleText}</div>
    );

    return (
      <div className="tickets-index-page__modal-title">
        <span>
          {I18n.t('tickets.form.edit_ticket', { id: selectedNode.id })}
          {' '}
          <Button
            type="text"
            icon={<LinkOutlined />}
            onClick={handleCopyTicketLink}
          />
        </span>
        {subtitle}
      </div>
    );
  };

  const statusOptions = statuses.map((status) => ({
    value: status,
    label: <StatusLabel status={status} />,
  }));

  const priorityOptions = priorities.map((priority) => ({
    value: priority,
    label: <PriorityLabel priority={priority} />,
  }));

  const cwoModuleOptions = cwoModules.map((cwoModule) => ({
    value: cwoModule,
    label: <CwoModuleLabel cwoModule={cwoModule} />,
  }));

  const ticketTypeOptions = ticketTypes.map((ticketType) => ({
    value: ticketType,
    label: <TicketTypeLabel type={ticketType} />,
  }));

  const commitmentLevelOptions = commitmentLevels.map((level) => ({
    value: level,
    label: <CommitmentLevelLabel commitmentLevel={level} />,
  }));

  const devStageOptions = devStages.map((stage) => ({
    value: stage,
    label: <DevStageLabel devStage={stage} />,
  }));

  const devStatusOptions = devStatuses.map((status) => ({
    value: status,
    label: <DevStatusLabel status={status} />,
  }));

  const sizeOptions = sizes.map((size) => ({
    value: size,
    label: <SizeLabel size={size} />,
  }));

  const isCurrentUserAuthor = selectedNode?.id
    ? (selectedNode?.author.id === currentUserInfo?.id
      && selectedNode?.author.type === currentUserInfo?.type)
    : true;

  const cwoModuleField = (
    <Form.Item
      label={I18n.t('activerecord.attributes.ticket.cwo_module')}
      name="cwo_module"
      validateStatus={errorStatus('cwo_module')}
      help={errorMessage('cwo_module')}
      className="tickets-index-page__form-item"
    >
      <Select options={cwoModuleOptions} />
    </Form.Item>
  );

  const reviewDecisionPending = isCurrentUserAuthor
    && !isNewTicket
    && currentStatus === 'on_review';

  const formActions = (
    <FormActions
      className={classNames(
        'tickets-index-page__ticket-form-submit-btn',
        {
          'tickets-index-page__ticket-form-submit-btn--single-column': useSingleColumn,
          'tickets-index-page__ticket-form-submit-btn--stacked': reviewDecisionPending,
        },
      )}
      isFormSending={isFormSending}
      isDecisionSending={isDecisionSending}
      submitButtonName={submitButtonName}
      reviewDecisionPending={reviewDecisionPending}
      onSubmit={() => form.submit()}
      onDecision={submitDecision}
    />
  );

  const singleColumnHiddenFieldNames = [
    'author_type', 'author_id', 'assignee_type', 'assignee_id',
    'responsible_user_id', 'priority', 'status', 'size',
  ];
  const singleColumnHiddenFields = singleColumnHiddenFieldNames.map((name) => (
    <Form.Item key={name} name={name} hidden><Input type="hidden" /></Form.Item>
  ));

  return (
    <Modal
      title={resolveModalTitle()}
      open={isModalOpen}
      onCancel={handleModalClose}
      footer={null}
      width={useSingleColumn ? 700 : 900}
      cancelText={I18n.t('helpers.submit.ticket.cancel')}
    >
      <Form
          /* eslint-disable camelcase */
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{
          priority: 'medium',
          status: 'open',
          author_type: currentUserInfo.type,
          assignee_type: initialAssignee.type,
          author_id: {
            value: currentUserInfo.id,
            label: userLabel(currentUserInfo.username, currentUserInfo.avatarUrl),
          },
          responsible_user_id: {
            value: responsibleUserInfo.id,
            label: userLabel(responsibleUserInfo.username, responsibleUserInfo.avatarUrl),
          },
          cwo_module: 'other',
          assignee_id: {
            value: initialAssignee.id,
            label: userLabel(initialAssignee.username, initialAssignee.avatarUrl),
          },
          company_id: currentUserInfo.companyId,
          ticket_type: 'bug',
          size: 'mid',
          cwo_context: {
            referrer_url: location.href,
          },
          /* eslint-enable camelcase */
        }}
      >
        <Row gutter={24}>
          <Col xs={24} md={useSingleColumn ? 24 : 16} lg={useSingleColumn ? 24 : 16}>
            <div className="tickets-index-page__tickets-form__left-column">
              <Form.Item name="company_id" hidden>
                <Input type="hidden" />
              </Form.Item>

              <Form.Item name="cwo_context" hidden>
                <Input type="hidden" />
              </Form.Item>

              <Form.Item
                label={I18n.t('activerecord.attributes.ticket.ticket_type')}
                name="ticket_type"
                style={{ marginBottom: 12 }}
              >
                {isMobile ? (
                  <Select
                    options={ticketTypeOptions}
                    disabled={!isCurrentUserAuthor && !isAdmin}
                  />
                ) : (
                  <Segmented
                    disabled={!isCurrentUserAuthor && !isAdmin}
                    options={ticketTypeOptions}
                    block
                  />
                )}
              </Form.Item>

              <Form.Item
                label={I18n.t('activerecord.attributes.ticket.title')}
                name="title"
                rules={[{ required: true, message: I18n.t('tickets.form.title_required') }]}
                validateStatus={errorStatus('title')}
                help={errorMessage('title')}
              >
                <TextArea
                  autoSize={{ minRows: 1, maxRows: 3 }}
                  disabled={!isCurrentUserAuthor}
                  placeholder={I18n.t('tickets.form.title_hint')}
                />
              </Form.Item>

              <Form.Item
                label={I18n.t('activerecord.attributes.ticket.description')}
                name="description"
                rules={[{ required: true, message: I18n.t('tickets.form.description_required') }]}
                validateStatus={errorStatus('description')}
                help={errorMessage('description')}
                className="tickets-index-page__form-item"
              >
                <TextArea
                  autoSize={{ minRows: 6, maxRows: 20 }}
                  placeholder={I18n.t('tickets.form.description_hint')}
                  disabled={!isCurrentUserAuthor}
                />
              </Form.Item>

              {useSingleColumn && cwoModuleField}

              <Form.Item
                label={I18n.t('tickets.form.external_links')}
                className="tickets-index-page__form-item"
              >
                <ExternalLinks
                  links={externalLinks}
                  onChange={setExternalLinks}
                />
              </Form.Item>

              {canManageJiraLink && (
                <Form.Item
                  label={I18n.t('activerecord.attributes.ticket.jira_link')}
                  name="jira_link"
                  validateStatus={errorStatus('jira_link')}
                  help={errorMessage('jira_link')}
                  className="tickets-index-page__form-item"
                >
                  <JiraLinkField key={selectedNode?.id ?? 'new'} />
                </Form.Item>
              )}

              {(isCurrentUserAuthor || attachments.length > 0 || attachmentsLoading) && (
                <Form.Item
                  label={I18n.t('tickets.form.attachments')}
                  name="files"
                  validateStatus={errorStatus('files')}
                  help={errorMessage('files')}
                  className="tickets-index-page__form-item"
                >
                  <Attachments
                    fileList={attachments}
                    setFileList={setAttachments}
                    isCurrentUserAuthor={isCurrentUserAuthor}
                    isLoading={attachmentsLoading}
                  />
                </Form.Item>
              )}

              {selectedNode?.id && (
              <>
                <hr className="tickets-index-page__horizontal-line" />
                <DetailedInfo
                  ticketId={selectedNode?.id}
                  currentUserInfo={currentUserInfo}
                  canSetInternalComment={canSetInternalComment}
                  onCommentDraftChange={setHasUnsavedComment}
                />
              </>
              )}

              {useSingleColumn && (
                <>
                  {singleColumnHiddenFields}
                  {formActions}
                </>
              )}

            </div>
          </Col>

          {!useSingleColumn && (
            <Col
              xs={24}
              md={8}
              lg={8}
              className="tickets-index-page__tickets-form__right-column-with-border"
            >
              <div className="tickets-index-page__tickets-form__right-column">
                <div className="tickets-index-page__tickets-form__right-column-content">
                  {selectedNode?.cwoContext?.referrer_url && (
                    <TicketContextInfo url={selectedNode.cwoContext.referrer_url} />
                  )}

                  <Form.Item name="author_type" hidden>
                    <Input type="hidden" />
                  </Form.Item>

                  <Form.Item
                    label={I18n.t('activerecord.attributes.ticket.author')}
                    name="author_id"
                    validateStatus={errorStatus('author')}
                    help={errorMessage('author')}
                    className="tickets-index-page__form-item"
                  >
                    <Select
                      labelInValue
                      placeholder={I18n.t('search_with_min_input', { count: 3 })}
                      showSearch
                      filterOption={false}
                      autoClearSearchValue={false}
                      onSearch={adminUserFetch}
                      notFoundContent={dataFetching ? <Spin size="small" /> : null}
                      options={adminUsers}
                      loading={dataFetching}
                      fieldNames={{ label: 'username', value: 'user_id' }}
                      disabled
                    />
                  </Form.Item>

                  <Form.Item
                    label={I18n.t('activerecord.attributes.ticket.responsible_user')}
                    name="responsible_user_id"
                    validateStatus={errorStatus('responsible_user_id')}
                    help={errorMessage('responsible_user_id')}
                    className="tickets-index-page__form-item"
                  >
                    <Select
                      labelInValue
                      placeholder={I18n.t('search_with_min_input', { count: 3 })}
                      showSearch
                      filterOption={false}
                      autoClearSearchValue={false}
                      onSearch={adminUserFetch}
                      notFoundContent={dataFetching ? <Spin size="small" /> : null}
                      options={adminUsers}
                      loading={dataFetching}
                      fieldNames={{ label: 'username', value: 'admin_user_id' }}
                      disabled
                    />
                  </Form.Item>

                  <Form.Item name="assignee_type" hidden>
                    <Input type="hidden" />
                  </Form.Item>

                  <Form.Item
                    label={I18n.t('activerecord.attributes.ticket.assignee')}
                    name="assignee_id"
                    validateStatus={errorStatus('assignee_id')}
                    help={errorMessage('assignee_id')}
                    className={assigneeFormItemClassName}
                  >
                    <Select
                      labelInValue
                      optionLabelProp="label"
                      placeholder={assigneeHint}
                      showSearch
                      filterOption={false}
                      autoClearSearchValue={false}
                      onSearch={assigneeUserFetch}
                      notFoundContent={assigneeNotFoundContent()}
                      loading={assigneeFetching}
                      disabled={currentUserInfo.type !== 'AdminUser'}
                      onChange={handleAssigneeChange}
                    >
                      {assigneeUsers.map((user) => (
                        <Select.Option
                          key={user.key}
                          value={user.id}
                          user_type={user.user_type}
                          label={(
                            <UserWithAvatarLabel
                              username={user.username}
                              avatarUrl={user.avatarUrl}
                            />
                        )}
                        >
                          <UserWithAvatarLabel
                            username={user.username}
                            avatarUrl={user.avatarUrl}
                          />
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>

                  {showAssignToMe && (
                    <Button
                      type="link"
                      size="small"
                      icon={<UserAddOutlined />}
                      className="tickets-index-page__assign-to-me"
                      onClick={() => selectAssignee(currentUserInfo)}
                    >
                      {I18n.t('tickets.form.assign_to_me')}
                    </Button>
                  )}

                  <Form.Item
                    label={I18n.t('activerecord.attributes.ticket.priority')}
                    name="priority"
                    validateStatus={errorStatus('priority')}
                    help={errorMessage('priority')}
                    className="tickets-index-page__form-item"
                  >
                    <Select
                      options={priorityOptions}
                      disabled={!isAdmin}
                    />
                  </Form.Item>

                  <Form.Item
                    label={I18n.t('activerecord.attributes.ticket.status')}
                    name="status"
                    validateStatus={errorStatus('status')}
                    help={errorMessage('status')}
                    className="tickets-index-page__form-item"
                  >
                    <Select
                      options={statusOptions}
                      disabled={!isAdmin}
                    />
                  </Form.Item>

                  {cwoModuleField}

                  <Form.Item
                    label={I18n.t('activerecord.attributes.ticket.size')}
                    name="size"
                    validateStatus={errorStatus('size')}
                    help={errorMessage('size')}
                    className="tickets-index-page__form-item"
                  >
                    <Select
                      options={sizeOptions}
                      disabled={!isAdmin}
                    />
                  </Form.Item>

                  {
                  currentTicketType === 'feature_request' && (
                    <>
                      <Form.Item
                        label={I18n.t('activerecord.attributes.ticket.commitment_level')}
                        name="commitment_level"
                        validateStatus={errorStatus('commitment_level')}
                        help={errorMessage('commitment_level')}
                        className="tickets-index-page__form-item"
                      >
                        <Select
                          options={commitmentLevelOptions}
                          disabled={!isAdmin}
                          allowClear
                        />
                      </Form.Item>

                      <Form.Item
                        label={I18n.t('activerecord.attributes.ticket.dev_stage')}
                        name="dev_stage"
                        validateStatus={errorStatus('dev_stage')}
                        help={errorMessage('dev_stage')}
                        className="tickets-index-page__form-item"
                      >
                        <Select
                          options={devStageOptions}
                          disabled={!isAdmin}
                          allowClear
                        />
                      </Form.Item>

                      <Form.Item
                        label={I18n.t('activerecord.attributes.ticket.dev_status')}
                        name="dev_status"
                        validateStatus={errorStatus('dev_status')}
                        help={errorMessage('dev_status')}
                        className="tickets-index-page__form-item"
                      >
                        <Select
                          options={devStatusOptions}
                          disabled={!isAdmin}
                          allowClear
                        />
                      </Form.Item>
                    </>
                  )
                }

                  <Form.Item
                    label={I18n.t('activerecord.attributes.ticket.due_date')}
                    name="due_date"
                    validateStatus={errorStatus('due_date')}
                    help={errorMessage('due_date')}
                    className="tickets-index-page__form-item"
                  >
                    <DatePicker
                      style={{ width: '100%' }}
                      format="LL"
                      disabled={!isAdmin}
                      allowClear
                    />
                  </Form.Item>

                  {selectedNode?.id && (
                  <Text
                    type="secondary"
                    className="tickets-index-page__form-created-at"
                  >
                    {dayjs(selectedNode.createdAt).format('LL HH:mm')}
                  </Text>
                  )}
                </div>

                {formActions}
              </div>
            </Col>
          )}
        </Row>
      </Form>
    </Modal>
  );
};

TicketModalForm.propTypes = {
  priorities: arrayOf(string),
  statuses: arrayOf(string),
  ticketTypes: arrayOf(string),
  selectedNode: selectedNodePropTypes,
  setSelectedNode: func,
  currentUserInfo: currentUserInfoPropTypes,
  setInitialTickets: func,
  isModalOpen: bool,
  setIsModalOpen: func,
  ticketFromDashboard: bool,
  responsibleUserInfo: responsibleUserInfoPropTypes,
  defaultBugAssigneeInfo: responsibleUserInfoPropTypes,
  commitmentLevels: arrayOf(string),
  devStages: arrayOf(string),
  devStatuses: arrayOf(string),
  sizes: arrayOf(string),
  cwoModules: arrayOf(string),
  canSetInternalComment: bool,
};

export default TicketModalForm;
